import { employeeLegalEntityId } from 'modules/core/lib/employeeLegalEntity'

import BulkEditForm from 'modules/employees/pages/EditInBulk/forms/BulkEdit'
import type {
  Change,
  CustomFieldsWithValues,
  EmployeeCustomFieldsValue,
} from 'modules/employees/pages/EditInBulk/types'
import { employeeEmployeeGroup } from 'modules/employees/store/collections/employeeEmployeeGroup'
import type Employee from 'modules/employees/store/models/Employee'
import permissionGroups from 'modules/permissions/store/collections/permissionGroups'
import memberships from 'modules/teams/store/collections/memberships'
import teams from 'modules/teams/store/collections/teams'
import { employeeTeams } from 'modules/teams/store/employeeTeams'

const getEmployeeTeams = (employeeId: number): number[] => {
  return employeeTeams(teams, memberships, employeeId).map((team) => team.attributes.id)
}

const getPermissionGroupId = (employee: Employee): number => {
  const employeePermissionGroup = permissionGroups.models.find((group) =>
    group.employees.find((emp) => emp.id === employee.id)
  )

  return employeePermissionGroup?.id
}

const getCustomFieldsValues = (
  customFieldsIds?: number[],
  employeeCustomFieldsValues?: EmployeeCustomFieldsValue[] | undefined,
  change?: Change
) => {
  const customFieldsValues: CustomFieldsWithValues = {}

  customFieldsIds?.forEach((customFieldId) => {
    const customFieldValue = employeeCustomFieldsValues?.find(
      (field) => field.fieldId === customFieldId
    )

    customFieldsValues[customFieldId] =
      change?.[customFieldId] ??
      (typeof customFieldValue?.centsValue ? customFieldValue?.centsValue : undefined) ??
      customFieldValue?.longTextValue ??
      customFieldValue?.dateValue ??
      customFieldValue?.singleChoiceValue ??
      ''
  })

  return customFieldsValues
}

interface MapChangeToBulkProps {
  employee: Employee
  change?: Change
  customFieldsIds?: number[]
  employeeCustomFieldsValues?: EmployeeCustomFieldsValue[]
}

const mapChangeToBulkForm = ({
  employee,
  change,
  customFieldsIds,
  employeeCustomFieldsValues,
}: MapChangeToBulkProps): BulkEditForm => {
  return new BulkEditForm({
    manager_id:
      change?.manager_id !== undefined ? change.manager_id : employee.attributes.manager_id ?? 0,
    location_id:
      change?.location_id !== undefined ? change.location_id : employee.attributes.location_id,
    timeoff_manager_id:
      change?.timeoff_manager_id !== undefined
        ? change.timeoff_manager_id
        : employee.attributes.timeoff_manager_id ?? 0,
    timeoff_policy_id:
      change?.timeoff_policy_id !== undefined
        ? change.timeoff_policy_id
        : employee.attributes.timeoff_policy_id ?? 0,
    regular_access_starts_on:
      change?.regular_access_starts_on !== undefined
        ? change.regular_access_starts_on
        : employee.attributes.regular_access_starts_on ?? '',
    teams_ids: change?.teams_ids !== undefined ? change.teams_ids : getEmployeeTeams(employee.id),
    legal_entity_id:
      change?.legal_entity_id !== undefined
        ? change.legal_entity_id
        : employeeLegalEntityId(employee),
    employee_group_id:
      change?.employee_group_id !== undefined
        ? change.employee_group_id
        : employeeEmployeeGroup(employee).id,
    permission_group_id:
      change?.permission_group_id !== undefined
        ? change.permission_group_id
        : getPermissionGroupId(employee),
    ...getCustomFieldsValues(customFieldsIds, employeeCustomFieldsValues, change),
  })
}

export default mapChangeToBulkForm
